import { Button, HelpText, Input, Label, Link, ValidationMessage } from "@gjensidige/builders-components";
import { useState, useRef } from "react";
import { validateSSN } from '@gjensidige/se-helpers';
import Personnummer from 'personnummer';
import { useAppTexts } from '../utils/appHelper'
import RegNrImg from '../images/reg-nr-image.svg';

export function CarForm() {

	const MIN_DRIVER_AGE = 18;
	const MIN_YEAR_PREFIX = 19;
	const NEXT_PAGE_URL_PATH = '/links/webshop-car';

	const { appTexts } = useAppTexts();

	const [regNumber, setRegNumber] = useState<string>('');
	const [validRegNumber, setValidRegNumber] = useState<boolean>(false);
	const [regNumberErrorText, setRegNumberErrorText] = useState<string>('');
	const [ssn, setSsn] = useState<string>('');
	const [validSsn, setValidSsn] = useState<boolean>(false);
	const [ssnErrorText, setSsnErrorText] = useState<string>('');

	const regNumberRef = useRef<HTMLInputElement>(null);
	const ssnRef = useRef<HTMLInputElement>(null);

	const ageResolver = (cpr: string) => Personnummer.parse(cpr).getAge();

	const focusFirstInvalidField = () => {
		if (!validRegNumber && regNumberRef.current) {
			regNumberRef.current.focus();
			regNumberRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		} else if (!validSsn && ssnRef.current) {
			ssnRef.current.focus();
			ssnRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	};

	const handleOnChangeSsn = (event: any) => {
		event.preventDefault();
		const customerInput = event.currentTarget.value.replace(/\D/g, '');
		setSsn(customerInput.toUpperCase());
		const isSsnValid = cprAndAgeValidator(customerInput);
		setValidSsn(isSsnValid);
		if (!customerInput.length) {
			setSsnErrorText('');
		}
	};

	const handleOnBlurSsnNumber = (event: any) => {
		event.preventDefault();
		const customerInput = event.currentTarget.value.replace(/\D/g, '');
		const isValidSsnFormat = validateSSN(customerInput)

		if (!isValidSsnFormat) {
			setSsnErrorText(appTexts.cprInvalidMessage);
			return;
		}

		const isValidAge = ageResolver(customerInput) >= MIN_DRIVER_AGE

		if (!isValidAge) {
			setSsnErrorText(appTexts.ageInvalidMessage);
		}
		else {
			setSsnErrorText(appTexts.cprInvalidMessage);
		}
	};

	const cprAndAgeValidator = (inputValue: any) => {
		if (
			(inputValue.length !== 10 && inputValue.length !== 12) ||
			(inputValue.length === 12 &&
				Number(inputValue.slice(0, 2)) < MIN_YEAR_PREFIX) ||
			!validateSSN(inputValue)
		) {
			return false;
		} else {
			if (ageResolver(inputValue) < MIN_DRIVER_AGE) {
				return false;
			} else return true;
		}
	};

	const handleOnChangeRegNo = (event: any) => {
		event.preventDefault();
		const customerInput = event.currentTarget.value.trim().toUpperCase();

		const validRegNumber = new RegExp(/([A-Z]{3})([0-9]{2})([0-9A-Z])/).test(
			customerInput
		);
		const finalValidRegNumber = customerInput.length === 6 && validRegNumber;
		setRegNumber(customerInput.toUpperCase());
		setValidRegNumber(finalValidRegNumber);
		if (!customerInput.length) {
			setRegNumberErrorText('');
		}
	};

	const handleOnBlurRegNo = (event: any) => {
		event.preventDefault();

		if (!validRegNumber) {
			setRegNumberErrorText(appTexts.regNumberInvalidMessage);
		}
	};

	const gotoNextStep = (event: any) => {
		if (validRegNumber && validSsn) {
			event.preventDefault();
			const customerInfo = {
				cprNumber: ssn,
				registrationNumber: regNumber
			};
			sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));
			if (NEXT_PAGE_URL_PATH) {
				window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
			}
		} else {
			event.preventDefault();
			!validRegNumber && setRegNumberErrorText(appTexts.regNumberInvalidMessage);
			!validSsn && setSsnErrorText(appTexts.cprInvalidMessage);
			
			setTimeout(focusFirstInvalidField, 0);
		}
	};

	const RegNrPrefixImage = () => <img src={RegNrImg} alt="" aria-hidden="true" />

	return (
		<form className="calculation-form">
			<div className="width-100">
				<Label className='text-white' htmlFor="registration-number">
					Registreringsnummer
				</Label>
				<Input
					ref={regNumberRef}
					autoComplete="on"
					id="registration-number"
					tracking
					type="text"
					prefix={RegNrPrefixImage()}
					value={regNumber}
					onChange={handleOnChangeRegNo}
					onBlur={handleOnBlurRegNo}
					invalid={!validRegNumber && !!regNumberErrorText}
					wrapperProps={{
						className: 'reg-nr-input-wrapper input-wrapper'
					}}
				/>
				{!validRegNumber && !!regNumberErrorText &&
					<ValidationMessage htmlFor="registration-number" invalid={true}>
						{regNumberErrorText}
					</ValidationMessage>
				}
			</div>
			<div className="sm-width-100">
				<Label className='text-white' htmlFor="ssn">
					Ditt personnummer
				</Label>
				<HelpText className='text-white' toggle="Varför behöver vi ditt personnummer?" htmlFor="ssn">
					Vi behöver ditt personnummer för att kunna ge dig rätt pris. Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
				</HelpText>
				<HelpText className='text-white' htmlFor="ssn">
					12 siffror ÅÅÅÅMMDDNNNN
				</HelpText>
				<Input
					ref={ssnRef}
					autoComplete="on"
					id="ssn"
					tracking
					inputMode="numeric"
					value={ssn}
					onChange={handleOnChangeSsn}
					onBlur={handleOnBlurSsnNumber}
					invalid={!validSsn && !!ssnErrorText}
					wrapperProps={{
						className: 'ssn-input-wrapper input-wrapper'
					}}
				/>
				{!validSsn && !!ssnErrorText &&
					<ValidationMessage htmlFor="ssn" invalid={true}>
						{ssnErrorText}
					</ValidationMessage>
				}
			</div>
			<Link
				target="_blank"
				rel="noopener noreferrer"
				href={'/om-oss/gdpr'}
				icon={false}
				className="text-white width-100"
			>
				Läs om hur vi behandlar personuppgifter
			</Link>
			<Button type='submit' size='md' variant="critical" onClick={gotoNextStep} aria-label="Gå vidare">Se ditt pris</Button>
		</form>
	)
}